'use client';

import { UnderscoreBtn } from '@/ui/common/Buttons/UnderscoreBtn';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { ETripView, TActivity } from '@/domain/Activities.domain';
import { CalendarAttractions, ActivitiesList } from './core';
import { cn } from '@/service/utils/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TCategories, TCategory } from '@/domain/Categories.domain';
import { CategoriesView } from './core/CategoriesView';
import { mapTripsByCategory } from '@/service/utils/mapTripsByCategory';
import { BreadCrumbs } from '@/ui/common/Breadcrumbs/Breadcrumbs';
import { scrollToTop } from '@/service/utils/scrollToId';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useStickyElementById } from '@/service/hooks/useStickyElementById';
import { SearchDate } from './core/SearchDate';
import { ActivitiesIcon } from '@/ui/common/Icons/Activities';
import { getQueryParams } from '@/service/utils/getQueryParams';

type TAttractionsContentProps = {
    partnerSlug: string;
    activities: TActivity[];
    activityIds: string;
    title: string;
    mainStatus?: string;
    showReviews?: boolean;
    categories?: TCategories;
};

const PARAM_NAME = 'category';
const FILTER = 'filter';

export const SHOW_ALL_CATEGORY = {
    id: '0',
    slug: 'all',
    title: 'All',
    has_image: false,
    trips: [],
};

const AttractionsContent = ({
    partnerSlug,
    activities,
    activityIds,
    title,
    mainStatus,
    showReviews,
    categories,
}: TAttractionsContentProps) => {
    const tripView = useAttractionsStore((state) => state.tripView);
    const updateListToShow = useAttractionsStore((state) => state.updateListToShow);
    const handleTripViewChange = useAttractionsStore((state) => state.handleTripViewChange);
    const { isLightframePage } = usePartnerStore();
    const [hideButtons, setHideButtons] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<TCategory | null>(null);
    const query = useSearchParams();
    const quearyParams = getQueryParams(query);
    const initAvailability = useAttractionsStore((state) => state.initAvailability);

    const isActivitiesView =
        tripView === ETripView.BROWSE_BY_ACTIVITY || tripView === ETripView.MOBILE_ACTIVITY;
    const isCalendarView = tripView === ETripView.CALENDAR;
    const isSearchView = tripView === ETripView.SEARCH_DATE;

    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();

    const categoriesList = useMemo(() => {
        if (categories?.length) {
            const list = categories.filter(({ trips }) => trips.length);

            return list.length ? list : undefined;
        }
        return;
    }, [categories]);

    const onSelectCategory = useCallback(
        (category: TCategory | null) => {
            const current = new URLSearchParams(Array.from(searchParams.entries()));

            const categoryName = category?.slug;
            const isSingleTrip = category?.trips.length === 1;

            if (!categoryName || isSingleTrip) {
                current.delete(PARAM_NAME);
            } else {
                current.set(PARAM_NAME, categoryName.toString());
            }

            const search = current.toString();
            const query = search ? `?${search}` : '';

            if (isSingleTrip) {
                router.push(`${pathname}/details/${category.trips[0]}${query}`);
            } else {
                scrollToTop();
                setSelectedCategory(category);
                updateListToShow(mapTripsByCategory(activities, category?.trips));
                router.push(`${pathname}${query}`);
            }
        },
        [pathname, searchParams, router, updateListToShow, activities]
    );

    useEffect(() => {
        initAvailability(activities, activityIds, quearyParams, partnerSlug);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const categoryName = searchParams.get(PARAM_NAME);
        const isSearchPage = searchParams.get(FILTER);

        if (categoryName && !isSearchPage) {
            const category =
                categoryName === SHOW_ALL_CATEGORY.slug
                    ? SHOW_ALL_CATEGORY
                    : categories?.find((item) => item.slug === categoryName);

            if (category) {
                onSelectCategory(category);
                handleTripViewChange(ETripView.BROWSE_BY_ACTIVITY);
            } else {
                onSelectCategory(null);
            }
        } else {
            setSelectedCategory(null);
        }

        if (isSearchPage) {
            handleTripViewChange(ETripView.SEARCH_DATE);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const { isSticky } = useStickyElementById(
        'sticky-buttons',
        isLightframePage
            ? {
                  offset: 55,
                  offsetDesktop: 80,
                  parentId: 'lightframe-container',
              }
            : { offset: 64 }
    );
    const isDateless = activities.every(
        (activity) => !activity.use_calendar && !activity.use_schedule
    );

    return (
        <div>
            {
                <div
                    className={cn(
                        'wrapper my-4 px-2 md:my-8 md:px-8',
                        isSearchView && 'md:relative',
                        !mainStatus && isSearchView && 'mb-0'
                    )}
                >
                    {isSearchView ? (
                        <div
                            className={cn(
                                'tx-medium-medium ml-auto mt-6 flex w-fit cursor-pointer items-center rounded-3xl border border-gray-3 bg-ash pr-4 text-gray-2 dark:border-blue-1 dark:bg-blue-dark dark:text-gray-3 md:absolute md:-top-3 md:right-0 lg:mr-8',
                                !mainStatus && 'mb-0 md:-top-10 md:right-8 lg:right-0'
                            )}
                            onClick={() => handleTripViewChange(ETripView.BROWSE_BY_ACTIVITY)}
                            data-test-id="browse-activites-btn"
                        >
                            <div className="my-0.5 ml-0.5 mr-2 flex h-[30px] w-[30px] items-center justify-center rounded-3xl bg-brand md:mr-1 lg:mr-2">
                                <ActivitiesIcon />
                            </div>
                            Browse Activites
                        </div>
                    ) : (
                        <h1
                            data-test-id="attractions-title"
                            className="tx-heading-4 mx-auto w-full max-w-max rounded-[27px] px-4 text-gray-1 md:tx-heading-2 dark:text-white md:px-[26px]"
                        >
                            {title}
                        </h1>
                    )}
                </div>
            }
            {!!mainStatus && (
                <div className="wrapper my-4 px-2 md:my-8 md:px-8">
                    <div
                        className={cn(
                            'tx-heading-4 mx-auto my-4 min-h-[44px] w-full rounded-2xl border-2 border-state-warning bg-sand px-2 py-3  text-center text-gray-1 dark:border-blue-8 dark:bg-dark-1 dark:text-white md:min-h-[56px] md:w-fit md:min-w-[440px] md:py-4 [&>a]:text-brand [&>a]:underline ',
                            isSearchView &&
                                'md:mx-auto md:min-w-[420px] md:max-w-[calc(100%-288px)] lg:max-w-[calc(100%-356px)]'
                        )}
                        data-test-id="activity-status"
                        dangerouslySetInnerHTML={{
                            __html: mainStatus,
                        }}
                    />
                </div>
            )}
            <div className={cn('relative w-full pt-9', isDateless && 'mt-9 pt-0')}>
                {!isSearchView && (
                    <div className="absolute left-0 top-0 min-h-full w-full">
                        {!!activityIds && !isDateless && (
                            <div
                                className={cn(
                                    'sticky mx-auto flex w-full justify-center rounded-b-xl transition-all',
                                    isLightframePage
                                        ? 'top-[55px] pt-[1px] md:top-[63px] lg:top-5 lg:mx-auto lg:w-max'
                                        : 'top-[55px] pt-[1px] md:top-[63px] lg:top-[14px] lg:mx-auto lg:w-max',
                                    hideButtons ? 'z-10' : 'z-40',
                                    isSticky
                                        ? 'bg-blue-main shadow-lightBottom dark:bg-dark-3 dark:shadow-darkBottom lg:z-50 lg:bg-transparent lg:shadow-none dark:lg:bg-transparent dark:lg:shadow-none'
                                        : ''
                                )}
                                id="sticky-buttons"
                            >
                                <UnderscoreBtn
                                    title={ETripView.BROWSE_BY_ACTIVITY}
                                    isActive={tripView === ETripView.BROWSE_BY_ACTIVITY}
                                    className={'hidden md:flex lg:mr-5'}
                                    onClick={() =>
                                        handleTripViewChange(ETripView.BROWSE_BY_ACTIVITY)
                                    }
                                    data-test-id="browse-by-activity-btn"
                                />
                                <UnderscoreBtn
                                    title={ETripView.MOBILE_ACTIVITY}
                                    isActive={tripView === ETripView.BROWSE_BY_ACTIVITY}
                                    className={'md:hidden'}
                                    onClick={() =>
                                        handleTripViewChange(ETripView.BROWSE_BY_ACTIVITY)
                                    }
                                    data-test-id="browse-by-activity-btn"
                                />
                                <UnderscoreBtn
                                    title={ETripView.CALENDAR}
                                    isActive={isCalendarView}
                                    onClick={() => handleTripViewChange(ETripView.CALENDAR)}
                                    data-test-id="calendar-view-btn"
                                />
                            </div>
                        )}
                    </div>
                )}
                {!!activityIds && (
                    <div className="relative">
                        {selectedCategory && !isSearchView && (
                            <div className="wrapper">
                                <BreadCrumbs
                                    items={[
                                        {
                                            path: `/${partnerSlug}`,
                                            label: 'Activities',
                                            onClick: () => onSelectCategory(null),
                                        },
                                        {
                                            path: '',
                                            label: selectedCategory.title,
                                            highlight: true,
                                        },
                                    ]}
                                />
                            </div>
                        )}
                        {isActivitiesView &&
                            (!selectedCategory && categoriesList?.length ? (
                                <CategoriesView
                                    categories={categoriesList}
                                    selectCategory={onSelectCategory}
                                />
                            ) : (
                                <ActivitiesList
                                    activities={activities}
                                    partnerSlug={partnerSlug}
                                    showReviews={showReviews}
                                />
                            ))}
                        {isCalendarView && (
                            <CalendarAttractions
                                activities={activities}
                                partnerSlug={partnerSlug}
                                activityIds={activityIds}
                                setHideButtons={setHideButtons}
                            />
                        )}
                        {isSearchView && (
                            <SearchDate
                                activities={activities}
                                partnerSlug={partnerSlug}
                                categoriesList={categoriesList}
                                selectedCategory={selectedCategory}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttractionsContent;
